import React from 'react';
import { useDrag, useDrop } from 'react-dnd';

const COLUMNS = ['now', 'next', 'later'];

function NowNextLaterView({ items, onItemClick, onItemUpdate }) {
  const getItemsByTimeframe = (timeframe) => {
    return items.filter((item) => item.timeframe === timeframe);
  };

  const handleDrop = async (item, newTimeframe) => {
    if (item.timeframe !== newTimeframe) {
      try {
        await onItemUpdate({ ...item, timeframe: newTimeframe });
      } catch (error) {
        console.error('Failed to update item:', error);
      }
    }
  };

  return (
    <div className="grid grid-cols-1 md:grid-cols-3 gap-6" data-testid="now-next-later-view">
      {COLUMNS.map((column) => (
        <Column
          key={column}
          timeframe={column}
          items={getItemsByTimeframe(column)}
          onItemClick={onItemClick}
          onDrop={handleDrop}
        />
      ))}
    </div>
  );
}

function Column({ timeframe, items, onItemClick, onDrop }) {
  const [{ isOver }, drop] = useDrop(
    () => ({
      accept: 'roadmap-item',
      drop: (item) => {
        onDrop(item, timeframe);
      },
      collect: (monitor) => ({
        isOver: monitor.isOver(),
      }),
    }),
    [timeframe, onDrop]
  );

  const titles = {
    now: 'Now',
    next: 'Next',
    later: 'Later',
  };

  return (
    <div
      ref={drop}
      className={`bg-card border-2 rounded-xl p-4 min-h-[600px] transition-all ${
        isOver ? 'border-primary bg-accent/5 shadow-lg' : 'border-border'
      }`}
      data-testid={`nnl-column-${timeframe}`}
    >
      <div className="flex items-center justify-between mb-4 pb-3 border-b-2 border-border">
        <h2 className="text-lg font-bold">{titles[timeframe]}</h2>
        <span className="text-sm text-muted-foreground bg-muted px-2 py-1 rounded-md">
          {items.length}
        </span>
      </div>
      <div className="space-y-3">
        {items.length === 0 ? (
          <div className="text-center py-12 text-muted-foreground">
            <p className="text-sm">No items {timeframe}</p>
            <p className="text-xs mt-1">Drag items here</p>
          </div>
        ) : (
          items.map((item) => (
            <DraggableItem
              key={item._id}
              item={item}
              onClick={() => onItemClick(item)}
            />
          ))
        )}
      </div>
    </div>
  );
}

function DraggableItem({ item, onClick }) {
  const [{ isDragging }, drag] = useDrag(
    () => ({
      type: 'roadmap-item',
      item: item,
      collect: (monitor) => ({
        isDragging: monitor.isDragging(),
      }),
    }),
    [item]
  );

  return (
    <div
      ref={drag}
      onClick={onClick}
      className={`bg-background border border-border rounded-lg p-4 cursor-move hover:shadow-md hover:border-primary transition-all ${
        isDragging ? 'opacity-30' : 'opacity-100'
      }`}
      data-testid={`item-${item._id}`}
    >
      <div className="flex items-start justify-between mb-2">
        <span
          className={`text-xs font-semibold px-2 py-1 rounded-md uppercase ${
            item.type === 'initiative'
              ? 'bg-blue-100 text-blue-800 dark:bg-blue-900/30 dark:text-blue-400'
              : item.type === 'epic'
              ? 'bg-green-100 text-green-800 dark:bg-green-900/30 dark:text-green-400'
              : 'bg-orange-100 text-orange-800 dark:bg-orange-900/30 dark:text-orange-400'
          }`}
        >
          {item.type}
        </span>
        <span
          className={`text-xs font-medium px-2 py-1 rounded-md capitalize ${
            item.status === 'planned'
              ? 'bg-gray-100 text-gray-700 dark:bg-gray-800 dark:text-gray-300'
              : item.status === 'in-progress'
              ? 'bg-blue-100 text-blue-700 dark:bg-blue-900/30 dark:text-blue-300'
              : item.status === 'done'
              ? 'bg-green-100 text-green-700 dark:bg-green-900/30 dark:text-green-300'
              : 'bg-yellow-100 text-yellow-700 dark:bg-yellow-900/30 dark:text-yellow-300'
          }`}
        >
          {item.status}
        </span>
      </div>
      <h3 className="font-semibold mb-2 text-sm">{item.title}</h3>
      {item.description && (
        <p className="text-xs text-muted-foreground line-clamp-2 mb-2">{item.description}</p>
      )}
      {item.rice_score && (
        <div className="mt-2 flex items-center gap-2">
          <span className="text-xs font-mono bg-primary/10 text-primary px-2 py-1 rounded">
            RICE: {item.rice_score.toFixed(1)}
          </span>
        </div>
      )}
      {item.tags && item.tags.length > 0 && (
        <div className="flex flex-wrap gap-1 mt-2">
          {item.tags.slice(0, 2).map((tag, i) => (
            <span
              key={i}
              className="text-xs bg-muted text-muted-foreground px-2 py-0.5 rounded"
            >
              {tag}
            </span>
          ))}
          {item.tags.length > 2 && (
            <span className="text-xs text-muted-foreground">+{item.tags.length - 2}</span>
          )}
        </div>
      )}
    </div>
  );
}

export default NowNextLaterView;
